import {connect} from 'react-redux';
import {ExpandMore} from '@material-ui/icons';
import {FormattedMessage} from 'react-intl';
import React, {createContext, useCallback, useEffect, useMemo, useState} from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionProps,
  AccordionSummary,
} from '@material-ui/core';

import {RootState} from '../../../../store';
import {DynamicForm} from '../../../DynamicForm';
import {getProjectData} from '../../../../store/project';
import {Prompts} from '../../../../types/prompts/Prompts';
import {setDeployConfig, useDeploy} from '../../useDeploy';
import {
  AnalyticsParametersAnswer,
  AnalyticsParametersPrompts,
} from '../../../../types/prompts/AnalyticsParameters';
import {DeploymentDetailsInfo} from '../../../../types/deployment/DeploymentDetailsInfo';
import {TestDeploymentQueryResponse} from '../../../../types/deployment/TestDeploymentQueryResponse';
import {Application} from '../../../../types/project/Application';

const AccordionContext = createContext<{setHasRows?: (hasRows: boolean) => void}>({});

type AnalyticsParametersProps = Omit<AccordionProps, 'children'> & {
  className?: string;
  isTaskInProgress: boolean;
  selectedApplication: Application | undefined;
  isItAnAnalyticsApplication: boolean | undefined;
  testSelectedDeployment: DeploymentDetailsInfo | null;
  historyItems: TestDeploymentQueryResponse | undefined;
  defaultAnalyticParameters: AnalyticsParametersPrompts;
  fetchedAnalyticParameters: AnalyticsParametersPrompts | undefined;
};

function AnalyticsParametersBase({
  historyItems,
  className = '',
  isTaskInProgress,
  selectedApplication,
  testSelectedDeployment,
  isItAnAnalyticsApplication,
  defaultAnalyticParameters = [],
  fetchedAnalyticParameters = [],
}: AnalyticsParametersProps) {
  const [hasRows, setHasRows] = useState(false);

  const [deployState, dispatch] = useDeploy();
  const {deployConfig} = useMemo(() => deployState, [deployState]);
  const providerValue = useMemo(() => ({hasRows, setHasRows}), [hasRows, setHasRows]);

  const historyItemSelectedAnalyticParameters: AnalyticsParametersAnswer = useMemo(() => {
    let historyItemSelectedAnalyticParameters: AnalyticsParametersAnswer = [];
    const historyItemAnalyticParameters = historyItems?.entries.find(
      deployment => deployment.id === testSelectedDeployment?.id
    )?.analyticsParameters;

    if (historyItemAnalyticParameters) {
      historyItemSelectedAnalyticParameters = Object.keys(
        historyItemAnalyticParameters
      ).map(key => ({
        key,
        value: historyItemAnalyticParameters[key],
      }));

      dispatch(
        setDeployConfig({
          analyticsParameters: historyItemSelectedAnalyticParameters,
        })
      );
    }

    return historyItemSelectedAnalyticParameters;
  }, [dispatch, historyItems, testSelectedDeployment?.id]);

  useEffect(() => {
    if (historyItemSelectedAnalyticParameters.length) {
      setDeployConfig({
        analyticsParameters: historyItemSelectedAnalyticParameters,
      });
    }
  }, [historyItemSelectedAnalyticParameters]);

  useEffect(
    function parseDefaultAnalyticParametersChanges() {
      if (
        defaultAnalyticParameters?.length &&
        !deployConfig.analyticsParameters?.length
      ) {
        dispatch(
          setDeployConfig({
            analyticsParameters: defaultAnalyticParameters?.map(
              ({key, prefilledValue}) => ({
                key,
                value: prefilledValue,
              })
            ),
          })
        );
      }
    },
    [defaultAnalyticParameters, deployConfig.analyticsParameters, dispatch]
  );

  useEffect(
    function parseFetchedAnalyticParametersChanges() {
      if (
        selectedApplication?.analyticsApplication &&
        !deployConfig.analyticsParameters.length &&
        fetchedAnalyticParameters &&
        fetchedAnalyticParameters.length
      ) {
        dispatch(
          setDeployConfig({
            analyticsParameters: fetchedAnalyticParameters?.map(
              ({key, prefilledValue}) => ({
                key,
                value: prefilledValue,
              })
            ),
          })
        );
      }
    },
    [
      dispatch,
      selectedApplication,
      fetchedAnalyticParameters,
      deployConfig.analyticsParameters,
    ]
  );

  const prompts = useMemo(() => {
    let prompts: AnalyticsParametersPrompts = fetchedAnalyticParameters || [];
    let params = fetchedAnalyticParameters?.map(({key, prefilledValue}) => ({
      key,
      value: prefilledValue,
    }));
    let analyticsParameters = params || deployConfig.analyticsParameters;

    if (analyticsParameters) {
      prompts = prompts.map(({key, prefilledValue, ...rest}) => {
        let parsedPrompt = {
          key,
          ...rest,
          disabled: isTaskInProgress || rest.disabled,
          placeholder: String(
            defaultAnalyticParameters.find(
              analyticsParameter => analyticsParameter.key === key
            )?.prefilledValue || ''
          ),
          prefilledValue:
            analyticsParameters.find(analyticsParameter => analyticsParameter.key === key)
              ?.value || prefilledValue,
        };

        if (rest.type === 'NUMBER') {
          const decimalCount = (String(prefilledValue).split('.')[1] || []).length;

          if (decimalCount) {
            parsedPrompt.inputProps = {
              step: `0.${'0'.repeat(decimalCount - 1)}1`,
              ...(rest.inputProps || {}),
            };
          }
        }

        return parsedPrompt;
      });
    }

    return prompts;
  }, [
    isTaskInProgress,
    fetchedAnalyticParameters,
    defaultAnalyticParameters,
    deployConfig.analyticsParameters,
  ]);

  const onBlur = useCallback(
    (formValues: Record<string, any>) => {
      const analyticsParameters: AnalyticsParametersAnswer = Object.keys(formValues).map(
        key => ({
          key,
          value: formValues[key],
        })
      );

      dispatch(
        setDeployConfig({
          analyticsParameters,
        })
      );
    },
    [dispatch]
  );

  const shouldRender = useMemo(() => isItAnAnalyticsApplication && prompts.length, [
    isItAnAnalyticsApplication,
    prompts,
  ]);

  return !shouldRender ? null : (
    <AccordionContext.Provider value={providerValue}>
      <Accordion elevation={0} defaultExpanded className={`deploy-settings ${className}`}>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          className="deploy-settings__title-container"
        >
          <FormattedMessage tagName="h4" id="analyticsParameters.title" />
        </AccordionSummary>

        <AccordionDetails className="deploy-settings__body_container">
          <DynamicForm
            onBlur={onBlur}
            prompts={prompts as Prompts[]}
            key={`form-${selectedApplication?.id}`}
            className="deploy-settings__form"
          />
        </AccordionDetails>
      </Accordion>
    </AccordionContext.Provider>
  );
}

const mapStateToProps = (state: RootState) => ({
  openProjectData: getProjectData(state),
});

export const AnalyticsParameters = connect(mapStateToProps)(AnalyticsParametersBase);
